import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

const WRITE_ROLES = ["ORG_ADMIN", "PROCUREMENT", "STORES"];

export default function Warehouses() {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const { companies } = useCompany() || {};
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/warehouses");
      setWarehouses(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const companyName = (id) => companies?.find((c) => c.id === id)?.name || id;

  return (
    <div className="space-y-6" data-testid="warehouses-page">
      <PageHeader title="Warehouses" description="Site stores and depots. Stock is tracked per item, per warehouse." action={canWrite && <CreateWarehouseDialog companies={companies} onCreated={load} />} />
      <div className="rounded-xl border border-border bg-card">
        {!loading && warehouses.length === 0 ? (
          <EmptyState title="No warehouses yet" testId="warehouses-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Company</TableHead><TableHead>City</TableHead></TableRow></TableHeader>
            <TableBody>
              {warehouses.map((w) => (
                <TableRow key={w.id} data-testid={`warehouse-row-${w.code}`}>
                  <TableCell className="font-mono text-xs text-foreground">{w.code}</TableCell>
                  <TableCell className="text-foreground">{w.name}</TableCell>
                  <TableCell className="text-muted-foreground">{companyName(w.company_id)}</TableCell>
                  <TableCell className="text-muted-foreground">{w.city || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateWarehouseDialog({ companies, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", code: "", name: "", city: "" });
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/inventory/warehouses", form);
      toast.success(`Warehouse ${form.code} created`);
      setForm({ company_id: "", code: "", name: "", city: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-warehouse-button"><Plus className="h-4 w-4" /> New Warehouse</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New Warehouse</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-warehouse-form">
          <div>
            <Label>Company</Label>
            <Select required value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
              <SelectTrigger data-testid="warehouse-company-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code</Label><Input required data-testid="warehouse-code-input" value={form.code} onChange={set("code")} className="mt-1.5" /></div>
            <div><Label>Name</Label><Input required data-testid="warehouse-name-input" value={form.name} onChange={set("name")} className="mt-1.5" /></div>
          </div>
          <div><Label>City</Label><Input data-testid="warehouse-city-input" value={form.city} onChange={set("city")} className="mt-1.5" /></div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-warehouse-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
